// Cart Next AJAX — talks to the WP endpoints (reuses the checkout-next `cn_auth`
// nonce + several shared cn_* endpoints). Envelope: { success, data }.

import { NONCE_REFUSED, readRefusal, refreshNonces } from '../utils/nonce-refresh';

const cfg = (typeof window !== 'undefined' && window.CART_NEXT) || {};

// Cart mutations run over the REST route (adp-cart/v1), NOT admin-ajax: an empty
// admin-ajax request costs 2.2-3.1s on this install because it boots WP in admin
// mode (63 plugins + 46 mu-plugins take their admin paths), while the same work
// over REST boots in ~0.6s. Both endpoints run the identical handler and return
// the identical { success, data } envelope, so admin-ajax stays as the fallback
// for the cases REST can't serve (route disabled, WAF rule, permalink oddity).
const REST_ROUTE = {
  cn_cart_get: 'get',
  cn_cart_qty: 'qty',
  cn_cart_add: 'add',
  cn_cart_apply_coupon: 'apply-coupon',
  cn_cart_remove_coupon: 'remove-coupon',
  cn_cart_shipping: 'shipping',
  cn_cart_select_shipping: 'select-shipping',
};

async function send(url, body, extraHeaders) {
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded', ...(extraHeaders || {}) },
    body,
  });
  // A tab that outlived its nonces: say so, so post() can refresh them and retry.
  if (res.status === 403 && await readRefusal(res)) return NONCE_REFUSED;
  // A REST route that 404s (or is blocked) must fall back, not surface as an error.
  if (!res.ok && res.status >= 400 && res.status !== 400) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

async function post(action, params, isRetry) {
  const body = new URLSearchParams();
  body.set('action', action);
  body.set('nonce', cfg.nonce || '');
  Object.keys(params || {}).forEach((k) => body.set(k, params[k] == null ? '' : params[k]));
  const payload = body.toString();

  // Only take the REST path when we have a wp_rest nonce to send with it: without
  // one WP silently treats a cookie-authenticated call as logged out, which for a
  // signed-in shopper would mean operating on a GUEST cart. admin-ajax has no such
  // rule, so it stays the safe fallback.
  const route = cfg.restUrl && cfg.restNonce && REST_ROUTE[action];
  let json;
  if (route) {
    try {
      // X-WP-Nonce keeps the logged-in cookie session authenticated on REST;
      // without it WP treats a cookie-authed REST call as logged out.
      json = await send(cfg.restUrl + route, payload, cfg.restNonce ? { 'X-WP-Nonce': cfg.restNonce } : {});
    } catch (e) { json = null; }
  }
  // A refused REST nonce skips admin-ajax: its cn_auth nonce dates from the same page load.
  if (!json) {
    try {
      json = await send(cfg.ajaxUrl, payload);
    } catch (e) {
      return { ok: false, data: { message: 'Πρόβλημα σύνδεσης. Δοκίμασε ξανά.' } };
    }
  }
  if (json === NONCE_REFUSED) {
    if (!isRetry && await refreshNonces(cfg)) return post(action, params, true);
    return { ok: false, data: { message: 'Πρόβλημα σύνδεσης. Δοκίμασε ξανά.' } };
  }
  return { ok: !!(json && json.success), data: (json && json.data) || {} };
}

// Authoritative cart payload for hydrate-on-mount.
export const getCart = (postcode) => post('cn_cart_get', { postcode });
// Change/remove a cart line (qty <= 0 removes). Returns the FULL cart-next
// payload (cart + teaser + coupons + shipping estimates + cross-sells + ETA).
export const setQty = (key, qty, postcode) => post('cn_cart_qty', { key, qty, postcode });
// Apply / remove a coupon.
export const applyCoupon = (code) => post('cn_cart_apply_coupon', { code });
export const removeCoupon = (code) => post('cn_cart_remove_coupon', { code });
// Add a suggested/cross-sell product to the cart.
export const addToCart = (productId, qty, device) => post('cn_cart_add', { product_id: productId, qty: qty || 1, device: device || '' });
// Recompute shipping-method estimates for a postcode.
export const cartShipping = (postcode) => post('cn_cart_shipping', { postcode });
// Pick a shipping group (pickup/courier/boxnow) → sets its cheapest rate as chosen.
export const selectShipping = (group, postcode) => post('cn_cart_select_shipping', { group, postcode });
